import { makeStyles } from "@material-ui/core";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import StatusLabel from "@saleor/components/StatusLabel";
import TableCellAvatar from "@saleor/components/TableCellAvatar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { InstagramList_instagrams_edges_node } from "@saleor/instagram/types/InstagramList";
import { InstagramListUrlSortField } from "@saleor/instagram/urls";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import moment from "moment-timezone";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface InstagramListProps
  extends ListProps,
    ListActions,
    SortPage<InstagramListUrlSortField> {
  instagram: InstagramList_instagrams_edges_node[];
}
const numberOfColumns = 9;

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      apply: {
        width: 80
      },
      colName: {
        width: "auto",
        textAlign: "left",
        overflow: "hidden", // 超出的文本隐藏
        textOverflow: "ellipsis", // 溢出用省略号显示
        whiteSpace: "nowrap" // 溢出不换行
      },
      colId: {
        width: "150",
        textAlign: "left"
      },
      colPublished: {
        textAlign: "left",
        width: 200
      },
      imgtitle: {
        width: "auto",
        textAlign: "center",
        paddingLeft: 50
      },
      displayoredr: {
        width: 80,
        textAlign: "center"
      },
      updatedate: {
        width: 160,
        textAlign: "center"
      }
    },
    apply: {
      color: "red"
    }
  }),
  { name: "CurrencyrateList" }
);
const Instagram: React.FC<InstagramListProps> = ({
  instagram,
  disabled,
  isChecked,
  onNextPage,
  onPreviousPage,
  onRowClick,
  pageInfo,
  selected,
  sort,
  toggle,
  toggleAll,
  toolbar,
  onSort,
  settings,
  onUpdateListSettings
}) => {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled}
        items={instagram}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader
          direction={
            sort.sort === InstagramListUrlSortField.instagramid
              ? getArrowDirection(sort.asc)
              : undefined
          }
          className={classes.colId}
          onClick={() => onSort(InstagramListUrlSortField.instagramid)}
        >
          <FormattedMessage defaultMessage="Content Id" />
        </TableCellHeader>
        <TableCellHeader>
          <FormattedMessage defaultMessage="Platform" />
        </TableCellHeader>
        <TableCellHeader>
          <FormattedMessage defaultMessage="Mime Type" />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === InstagramListUrlSortField.shortcode
              ? getArrowDirection(sort.asc)
              : undefined
          }
          className={classes.colName}
          onClick={() => onSort(InstagramListUrlSortField.shortcode)}
        >
          <FormattedMessage defaultMessage="Short Code" />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === InstagramListUrlSortField.image
              ? getArrowDirection(sort.asc)
              : undefined
          }
          className={classes.imgtitle}
          onClick={() => onSort(InstagramListUrlSortField.image)}
        >
          <FormattedMessage defaultMessage="Image" />
        </TableCellHeader>

        <TableCellHeader
          direction={
            sort.sort === InstagramListUrlSortField.name
              ? getArrowDirection(sort.asc)
              : undefined
          }
          className={classes.colName}
          onClick={() => onSort(InstagramListUrlSortField.name)}
        >
          <FormattedMessage defaultMessage="UserName" />
        </TableCellHeader>

        <TableCell className={classes.colPublished}>
          <FormattedMessage defaultMessage="Published" />
        </TableCell>

        {/* <TableCell className={classes.colName} style={{textAlign:"center",padding:0,margin:0}}>
                    <FormattedMessage defaultMessage="istop" />
                </TableCell> */}

        <TableCellHeader
          direction={
            sort.sort === InstagramListUrlSortField.createTime
              ? getArrowDirection(sort.asc)
              : undefined
          }
          className={classes.colName}
          onClick={() => onSort(InstagramListUrlSortField.createTime)}
          textAlign={"center"}
        >
          <FormattedMessage defaultMessage="createTime" />
        </TableCellHeader>
      </TableHead>

      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>

      <TableBody>
        {renderCollection(
          instagram,
          instagram => {
            const isSelected = instagram ? isChecked(instagram.id) : false;
            return (
              <TableRow
                hover={!!instagram}
                onClick={instagram && onRowClick(instagram.id)}
                key={instagram ? instagram.id : "skeleton"}
                selected={isSelected}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(instagram.id)}
                  />
                </TableCell>

                <TableCell className={classes.colId} data-test="id">
                  {maybe<React.ReactNode>(
                    () => instagram.instagramId,
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colName} data-test="id">
                  {maybe<React.ReactNode>(
                    () => instagram.platForm,
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colName} data-test="id">
                  {maybe<React.ReactNode>(
                    () => instagram.mimeType,
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colName} data-test="id">
                  {maybe<React.ReactNode>(
                    () => instagram.shortcode,
                    <Skeleton />
                  )}
                </TableCell>

                <TableCellAvatar
                  thumbnail={maybe(() => instagram.imageThumb)}
                />

                <TableCell className={classes.colName} data-test="name">
                  {maybe<React.ReactNode>(
                    () => instagram.username,
                    <Skeleton />
                  )}
                </TableCell>

                {/* <TableCell padding="checkbox" style={{ textAlign: "center" }}>
                  <Checkbox checked={maybe(() => instagram.published)} />
                </TableCell> */}

                <TableCell
                  className={classes.colPublished}
                  data-test="published"
                  data-test-is-published={maybe(() => instagram.published)}
                >
                  {instagram &&
                  maybe(() => instagram.published !== undefined) ? (
                    <StatusLabel
                      label={
                        instagram.published
                          ? intl.formatMessage({
                              defaultMessage: "Published",
                              description: "instagram status"
                            })
                          : intl.formatMessage({
                              defaultMessage: "Not published",
                              description: "instagram status"
                            })
                      }
                      status={instagram.published ? "success" : "error"}
                    />
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                {/* <TableCell  padding="checkbox" style={{textAlign:"center"}}>
                                    <Checkbox
                                        checked={maybe(() => instagram.istop)}
                                    />
                                </TableCell> */}

                <TableCell className={classes.colName}>
                  {instagram ? (
                    moment(instagram.createTime).format("YYYY-MM-DD HH:mm:ss")
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow data-test="emptyListMessage">
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No instagram found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};
export default Instagram;
